// src/services/eventsService.js
// CRUD de events + helpers de relaciones (event_contacts, event_venues) y lectura asociada.
// Objetivo: encapsular Supabase queries sin acoplar a UI/Alpine.
// Nota: NO fuerzo mapeos/whitelists automáticamente para no “cambiarte” nada sin permiso.

import { getSupabaseClient, normalizeSupabaseError } from "./supabaseClient.js";

// =========================
// Tablas (centralizadas)
// =========================
export const TABLES = Object.freeze({
  EVENTS: "events",
  EVENT_CONTACTS: "event_contacts",
  CONTACTS: "contacts",
  EVENT_VENUES: "event_venues",
  VENUES: "venues",
  EVENT_FORECASTS: "event_expense_forecasts", // 1:1 con events
});

// =========================
// Helpers internos
// =========================
function assertId(id, name = "id") {
  if (id === null || id === undefined || String(id).trim() === "") {
    throw new Error(`${name} es obligatorio.`);
  }
}

function pickColumns(obj, whitelist) {
  if (!whitelist) return { ...obj };
  const out = {};
  for (const k of whitelist) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k];
  }
  return out;
}

function throwSupabase(error, context = "") {
  if (!error) return;
  const n = normalizeSupabaseError(error);
  const msg = context ? `${context}: ${n.message}` : n.message;
  const e = new Error(msg);
  e.code = n.code;
  e.details = n.details;
  e.hint = n.hint;
  e.raw = n.raw;
  throw e;
}

// =========================
// EVENTS: lecturas
// =========================

/**
 * Lista eventos por team_id (orden por date asc por defecto).
 */
export async function listEventsByTeam(teamId, { orderBy = "date", ascending = true, select = "*" } = {}) {
  assertId(teamId, "teamId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENTS)

    // Hacemos left join con forecasts (1:1)
    // select: "*, event_expense_forecasts ( forecast_total, forecast_by_category )"
    // Pero el usuario pasó 'select' param. Intentamos inyectar o asumir que user pide *.
    // Para no romper llamadas existentes, si select="*", lo expandimos.
    .select(select === "*" ? "*, event_expense_forecasts(forecast_total), event_venues(is_primary, venues(name, address, city, region))" : select)
    .eq("team_id", teamId)
    .order(orderBy, { ascending });

  throwSupabase(error, "Error listando events");

  // Mapeo de conveniencia para forecast y venue
  const rows = data || [];
  return rows.map(e => {
    let forecast = 0;
    // Si viene como array (1:1 relation a veces devuelve array) o objeto
    const fRel = e.event_expense_forecasts;
    if (Array.isArray(fRel)) {
      forecast = fRel.length > 0 ? Number(fRel[0].forecast_total) : 0;
    } else if (fRel && typeof fRel === 'object') {
      forecast = Number(fRel.forecast_total) || 0;
    }

    // Extraer nombre del venue primario si existe
    let primaryVenue = null;
    const vRel = e.event_venues;
    if (Array.isArray(vRel)) {
      const primary = vRel.find(v => v.is_primary);
      if (primary && primary.venues) {
        primaryVenue = primary.venues;
      }
    }

    // Inyectamos validado
    // Nota: mantenemos estructura original, solo agregamos prop de conveniencia
    return {
      ...e,
      forecast_expense: forecast,
      primary_venue: primaryVenue,
      primary_venue_name: primaryVenue?.name || null
    };
  });
}

/**
 * Lista TODOS los eventos de todos los equipos (Solo para managers).
 */
export async function listAllEvents({ orderBy = "date", ascending = true, select = "*" } = {}) {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENTS)
    .select(select === "*" ? "*, event_expense_forecasts(forecast_total), event_venues(is_primary, venues(name, address, city, region))" : select)
    .order(orderBy, { ascending });

  throwSupabase(error, "Error listando todos los events");

  const rows = data || [];
  return rows.map(e => {
    let forecast = 0;
    const fRel = e.event_expense_forecasts;
    if (Array.isArray(fRel)) {
      forecast = fRel.length > 0 ? Number(fRel[0].forecast_total) : 0;
    } else if (fRel && typeof fRel === 'object') {
      forecast = Number(fRel.forecast_total) || 0;
    }

    let primaryVenue = null;
    const vRel = e.event_venues;
    if (Array.isArray(vRel)) {
      const primary = vRel.find(v => v.is_primary);
      if (primary && primary.venues) {
        primaryVenue = primary.venues;
      }
    }
    return { ...e, forecast_expense: forecast, primary_venue: primaryVenue, primary_venue_name: primaryVenue?.name || null };
  });
}


/**
 * Lista eventos activos (no rechazados) en una fecha concreta.
 * Útil para detectar conflictos de fecha / overbooking.
 *
 * @param {string} date - Fecha en formato ISO (YYYY-MM-DD)
 * @param {object} [opts]
 * @param {string} [opts.excludeId] - ID de evento a excluir (para no mostrarse a sí mismo)
 * @returns {Promise<Array>} eventos activos en esa fecha
 */
export async function listEventsByDate(date, { excludeId = null } = {}) {
  if (!date) return [];
  const supabase = getSupabaseClient();

  let query = supabase
    .from(TABLES.EVENTS)
    .select("id, client_name_legacy, stage, type, date, team_id, amount, region, channel, owner_id, event_expense_forecasts(forecast_total), event_venues(is_primary, venues(name, address, city, region))")
    .eq("date", date)
    .not("stage", "in", '("rechazado","lost","rejected")');

  if (excludeId) query = query.neq("id", excludeId);

  const { data, error } = await query.order("stage", { ascending: true });
  throwSupabase(error, "Error buscando eventos en la misma fecha");
  return (data || []).map(e => {
    let forecast = 0;
    const fRel = e.event_expense_forecasts;
    if (Array.isArray(fRel)) {
      forecast = fRel.length > 0 ? Number(fRel[0].forecast_total) : 0;
    } else if (fRel && typeof fRel === "object") {
      forecast = Number(fRel.forecast_total) || 0;
    }

    let primaryVenue = null;
    const vRel = e.event_venues;
    if (Array.isArray(vRel)) {
      const primary = vRel.find(v => v.is_primary);
      if (primary?.venues) primaryVenue = primary.venues;
    }

    return {
      ...e,
      forecast_expense: forecast,
      primary_venue: primaryVenue,
      primary_venue_name: primaryVenue?.name || null,
    };
  });
}


/**
 * Obtiene un evento por id (sin joins).
 */
export async function getEventById(eventId, { select = "*" } = {}) {
  assertId(eventId, "eventId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.from(TABLES.EVENTS).select(select).eq("id", eventId).single();
  throwSupabase(error, "Error leyendo event");
  return data;
}

// =========================
// EVENTS: escrituras
// =========================

/**
 * Crea un evento.
 * - No añade created_by/team_id automáticamente (para no imponerte nada).
 *   Pásalos en payload si tu schema lo exige.
 *
 * options:
 * - whitelist: string[] -> si quieres forzar columnas permitidas (opcional)
 * - select: string -> qué devolver tras insert
 */
export async function createEvent(payload, { whitelist = null, select = "*" } = {}) {
  if (!payload || typeof payload !== "object") throw new Error("payload inválido.");
  const supabase = getSupabaseClient();

  const insertPayload = pickColumns(payload, whitelist);

  const { data, error } = await supabase
    .from(TABLES.EVENTS)
    .insert(insertPayload)
    .select(select)
    .single();

  throwSupabase(error, "Error creando event");
  return data;
}

/**
 * Actualiza un evento por id.
 * options:
 * - whitelist: string[]
 */
export async function updateEvent(eventId, patch, { whitelist = null } = {}) {
  assertId(eventId, "eventId");
  if (!patch || typeof patch !== "object") throw new Error("patch inválido.");

  const supabase = getSupabaseClient();
  const updatePayload = pickColumns(patch, whitelist);

  const { error } = await supabase.from(TABLES.EVENTS).update(updatePayload).eq("id", eventId);
  throwSupabase(error, "Error actualizando event");
  return true;
}

/**
 * Borra un evento por id.
 */
export async function deleteEvent(eventId) {
  assertId(eventId, "eventId");
  const supabase = getSupabaseClient();

  const { error } = await supabase.from(TABLES.EVENTS).delete().eq("id", eventId);
  throwSupabase(error, "Error borrando event");
  return true;
}

/**
 * Actualiza stage de un evento (drag&drop).
 */
export async function setEventStage(eventId, stage) {
  assertId(eventId, "eventId");
  const supabase = getSupabaseClient();
  const now = new Date().toISOString();

  const patch = {
    stage,
    stage_updated_at: now,
  };

  // Si se pasa a confirmado o done, registramos el momento de cierre.
  // Si se saca de confirmado o done, limpiamos won_time.
  if (stage === "confirmed" || stage === "done") {
    patch.won_time = now;
  } else {
    patch.won_time = null;
  }

  if (stage === "confirmed") {
    patch.confirmed_at = now;
    patch.deposit_received_at = now;
  }

  if (stage === "done") {
    const { data: authData } = await supabase.auth.getUser();
    patch.done_at = now;
    patch.done_by = authData?.user?.id || null;
  } else {
    patch.done_at = null;
    patch.done_by = null;
  }

  const { error } = await supabase.from(TABLES.EVENTS).update(patch).eq("id", eventId);
  throwSupabase(error, "Error actualizando stage");
  return true;
}

// =========================
// EVENT_CONTACTS
// =========================

/**
 * Carga contactos asociados a un evento, con join a contacts.
 * Devuelve filas de event_contacts con `contacts` embebido (alias).
 */
export async function listEventContacts(eventId) {
  assertId(eventId, "eventId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_CONTACTS)
    .select("id, role_in_event, is_primary, notes, contact_id, contacts:contact_id (id, type, name, email, phone)")
    .eq("event_id", eventId)
    .order("is_primary", { ascending: false });

  throwSupabase(error, "Error cargando event_contacts");
  return data || [];
}

/**
 * Lista eventos asociados a un contacto.
 */
export async function listEventsByContact(contactId) {
  assertId(contactId, "contactId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_CONTACTS)
    .select("event_id, events:event_id(*)")
    .eq("contact_id", contactId);

  throwSupabase(error, "Error listando eventos del contacto");
  return (data || []).filter(d => !!d.events).map(d => d.events);
}


/**
 * Marca un contacto como primary en un evento.
 * Estrategia:
 * 1) set is_primary=false a todos los links del evento
 * 2) si ya existe link (event_id, contact_id) => update is_primary=true (+ role/notes opcional)
 *    si no existe => insert (event_id, contact_id, is_primary=true, role_in_event/notes opcional)
 *
 * options:
 * - role_in_event: string|null
 * - notes: string|null
 */
export async function upsertPrimaryEventContact(eventId, contactId, options = {}) {
  assertId(eventId, "eventId");
  assertId(contactId, "contactId");
  const supabase = getSupabaseClient();

  // 1) desmarcar otros primarios
  {
    const { error } = await supabase
      .from(TABLES.EVENT_CONTACTS)
      .update({ is_primary: false })
      .eq("event_id", eventId);

    throwSupabase(error, "Error desmarcando contactos primarios");
  }

  // 2) buscar link existente
  const { data: existingLink, error: findErr } = await supabase
    .from(TABLES.EVENT_CONTACTS)
    .select("id")
    .eq("event_id", eventId)
    .eq("contact_id", contactId)
    .maybeSingle();

  throwSupabase(findErr, "Error buscando vínculo event_contact");

  const patch = {
    is_primary: true,
  };
  if (Object.prototype.hasOwnProperty.call(options, "role_in_event")) patch.role_in_event = options.role_in_event;
  if (Object.prototype.hasOwnProperty.call(options, "notes")) patch.notes = options.notes;

  if (existingLink?.id) {
    const { error } = await supabase.from(TABLES.EVENT_CONTACTS).update(patch).eq("id", existingLink.id);
    throwSupabase(error, "Error actualizando vínculo event_contact (primary)");
    return { id: existingLink.id, event_id: eventId, contact_id: contactId, ...patch };
  }

  const insertPayload = {
    event_id: eventId,
    contact_id: contactId,
    ...patch,
  };

  const { data, error } = await supabase.from(TABLES.EVENT_CONTACTS).insert(insertPayload).select("*").single();
  throwSupabase(error, "Error insertando vínculo event_contact (primary)");
  return data;
}

/**
 * Añade un contacto a un evento (como secundario por defecto).
 */
export async function addEventContact(eventId, contactId, { role_in_event = null, notes = null, is_primary = false } = {}) {
  assertId(eventId, "eventId");
  assertId(contactId, "contactId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_CONTACTS)
    .insert({ event_id: eventId, contact_id: contactId, role_in_event, notes, is_primary })
    .select("*")
    .single();

  throwSupabase(error, "Error vinculando contacto");
  return data;
}

/**
 * Elimina un vínculo event_contact.
 */
export async function removeEventContact(eventContactId) {
  assertId(eventContactId, "eventContactId");
  const supabase = getSupabaseClient();

  const { error } = await supabase.from(TABLES.EVENT_CONTACTS).delete().eq("id", eventContactId);
  throwSupabase(error, "Error eliminando vínculo de contacto");
  return true;
}

// =========================
// EVENT_VENUES
// =========================

/**
 * Devuelve venue_id primary (o null).
 */
export async function getPrimaryVenueId(eventId) {
  assertId(eventId, "eventId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_VENUES)
    .select("venue_id, is_primary")
    .eq("event_id", eventId)
    .eq("is_primary", true)
    .maybeSingle();

  throwSupabase(error, "Error cargando venue primary");
  return data?.venue_id || null;
}

/**
 * Devuelve el venue primary de un evento con sus datos, o null.
 *
 * El contrato cita el sitio por su nombre ("Finca Las Margas, Segovia"), no
 * por el texto libre de events.location, que suele traer indicaciones sueltas.
 */
export async function getPrimaryVenueForEvent(eventId) {
  assertId(eventId, "eventId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_VENUES)
    .select("is_primary, venues:venue_id (id, name, address, city, region)")
    .eq("event_id", eventId)
    .eq("is_primary", true)
    .maybeSingle();

  throwSupabase(error, "Error cargando venue primary del evento");
  return data?.venues || null;
}

/**
 * Nombre del sitio tal y como debe leerse en un documento: "Finca X, Ciudad".
 * Devuelve "" si no hay venue.
 */
export function formatVenueForDocument(venue) {
  if (!venue) return "";
  // Ciudad y provincia se omiten si ya estan dentro del nombre, para no
  // acabar escribiendo "Finca Las Margas Segovia, Segovia".
  const name = (venue.name || "").trim();
  const extras = [venue.city, venue.region]
    .map((s) => (s || "").trim())
    .filter((s) => s && !name.toLowerCase().includes(s.toLowerCase()));

  return [name, ...new Set(extras)].filter(Boolean).join(", ");
}

/**
 * Upsert del venue primary para un evento.
 * NOTA: tu schema parece exigir team_id en event_venues; por eso se pide teamId.
 */
export async function upsertPrimaryEventVenue(teamId, eventId, venueId) {
  assertId(teamId, "teamId");
  assertId(eventId, "eventId");
  assertId(venueId, "venueId");

  const supabase = getSupabaseClient();

  // 1) desmarcar otros primarios
  {
    const { error } = await supabase
      .from(TABLES.EVENT_VENUES)
      .update({ is_primary: false })
      .eq("event_id", eventId);

    throwSupabase(error, "Error desmarcando venues primarios");
  }

  // 2) buscar link existente
  const { data: existing, error: findErr } = await supabase
    .from(TABLES.EVENT_VENUES)
    .select("id")
    .eq("event_id", eventId)
    .eq("venue_id", venueId)
    .maybeSingle();

  throwSupabase(findErr, "Error buscando vínculo event_venues");

  if (existing?.id) {
    const { error } = await supabase.from(TABLES.EVENT_VENUES).update({ is_primary: true }).eq("id", existing.id);
    throwSupabase(error, "Error actualizando vínculo event_venues (primary)");
    return { id: existing.id, team_id: teamId, event_id: eventId, venue_id: venueId, is_primary: true };
  }

  const { data, error } = await supabase
    .from(TABLES.EVENT_VENUES)
    .insert({
      team_id: teamId,
      event_id: eventId,
      venue_id: venueId,
      is_primary: true,
    })
    .select("*")
    .single();

  throwSupabase(error, "Error insertando vínculo event_venues (primary)");
  return data;
}

// =========================
// Guardado compuesto (opcional)
// =========================

/**
 * Guarda un evento y (opcionalmente) asegura relaciones primarias:
 * - contact primary (event_contacts)
 * - venue primary (event_venues)
 *
 * params:
 * - mode: "create" | "update"
 * - eventPayload: object
 * - eventId: id si mode="update"
 * - teamId: requerido si se va a tocar event_venues
 * - primaryContactId: opcional
 * - primaryVenueId: opcional
 *
 * options:
 * - eventWhitelist: string[] (si quieres whitelist en events)
 * - select: string (retorno al crear)
 */
export async function saveEventWithPrimaries({
  mode,
  eventPayload,
  eventId = null,
  teamId = null,
  primaryContactId = null,
  primaryVenueId = null,
  options = {},
} = {}) {
  if (mode !== "create" && mode !== "update") throw new Error('mode debe ser "create" o "update".');
  if (!eventPayload || typeof eventPayload !== "object") throw new Error("eventPayload inválido.");

  let savedId = eventId;

  if (mode === "create") {
    const created = await createEvent(eventPayload, {
      whitelist: options.eventWhitelist ?? null,
      select: options.select ?? "*",
    });
    savedId = created?.id;
    if (!savedId) throw new Error("No se obtuvo id del evento creado.");
  } else {
    assertId(eventId, "eventId");
    await updateEvent(eventId, eventPayload, { whitelist: options.eventWhitelist ?? null });
    savedId = eventId;
  }

  // Relations (si se pasan)
  if (primaryContactId) {
    await upsertPrimaryEventContact(savedId, primaryContactId, options.contactOptions || {});
  }

  if (primaryVenueId) {
    assertId(teamId, "teamId (requerido para event_venues)");
    await upsertPrimaryEventVenue(teamId, savedId, primaryVenueId);
  }

  return savedId;
}
